// src/contexts/AuthContext.js - FIXED: Better error handling for login failures
import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
  useMemo,
} from "react";
import authService from "../services/authService";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [token, setToken] = useState(null);
  const [securityStatus, setSecurityStatus] = useState(null);
  const [securityLevel, setSecurityLevel] = useState("basic");

  // Login flow states - FIXED: Better state management
  const [loginFlow, setLoginFlow] = useState(null);
  const [pendingCredentials, setPendingCredentials] = useState(null);
  const [loginGuidance, setLoginGuidance] = useState(null);

  // Prevent race conditions and multiple simultaneous operations
  const loadingUserRef = useRef(false);
  const securityStatusLoadedRef = useRef(false);
  const initializationRef = useRef(false);
  const mountedRef = useRef(true);

  // Cleanup timeouts and intervals
  const timeoutsRef = useRef(new Set());
  const intervalsRef = useRef(new Set());

  // Cleanup function
  const cleanup = useCallback(() => {
    mountedRef.current = false;
    loadingUserRef.current = false;
    securityStatusLoadedRef.current = false;
    initializationRef.current = false;

    // Clear all timeouts and intervals
    timeoutsRef.current.forEach(clearTimeout);
    intervalsRef.current.forEach(clearInterval);
    timeoutsRef.current.clear();
    intervalsRef.current.clear();
  }, []);

  // Safe state updates (only if component is mounted)
  const safeSetState = useCallback((setter, value) => {
    if (mountedRef.current) {
      setter(value);
    }
  }, []);

  // Safe timeout creation
  const safeSetTimeout = useCallback((callback, delay) => {
    if (!mountedRef.current) return null;

    const timeoutId = setTimeout(() => {
      timeoutsRef.current.delete(timeoutId);
      if (mountedRef.current) {
        callback();
      }
    }, delay);

    timeoutsRef.current.add(timeoutId);
    return timeoutId;
  }, []);

  // Safe interval creation
  const safeSetInterval = useCallback((callback, delay) => {
    if (!mountedRef.current) return null;

    const intervalId = setInterval(() => {
      if (mountedRef.current) {
        callback();
      } else {
        clearInterval(intervalId);
        intervalsRef.current.delete(intervalId);
      }
    }, delay);

    intervalsRef.current.add(intervalId);
    return intervalId;
  }, []);

  // Helper functions
  const isTokenValid = useCallback((token) => {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp > currentTime + 300; // 5 minutes buffer
    } catch {
      return false;
    }
  }, []);

  const fetchFreshUserDataInBackground = useCallback(async () => {
    try {
      const userData = await authService.getCurrentUser();
      if (userData.success && !userData.cached && mountedRef.current) {
        setUser(userData.user);
        // Update stored user data
        authService.clearCache(); // Clear cache to get fresh data next time
      }
    } catch (error) {
      // Don't throw on background fetch failure
      console.warn("Background user fetch failed:", error);
    }
  }, []);

  // FIXED: Clear authentication state - MOVED UP BEFORE USE
  const clearAuthState = useCallback(() => {
    if (mountedRef.current) {
      setUser(null);
      setToken(null);
      setSecurityStatus(null);
      setSecurityLevel("basic");
    }
    securityStatusLoadedRef.current = false;
    loadingUserRef.current = false;
  }, []);

  // FIXED: Clear login flow states - MOVED UP BEFORE USE
  const clearLoginStates = useCallback(() => {
    if (mountedRef.current) {
      setLoginFlow(null);
      setPendingCredentials(null);
      setLoginGuidance(null);
    }
  }, []);

  const handleTokenExpired = useCallback(async () => {
    try {
      const refreshed = await authService.refreshToken();
      if (refreshed && mountedRef.current) {
        const newToken = authService.getToken();
        setToken(newToken);
        return true;
      }
    } catch (error) {
      console.warn("Token refresh failed:", error);
    }

    // If refresh fails, clear everything
    clearAuthState();
    return false;
  }, [clearAuthState]);

  // Load security status only when needed (lazy loading)
  const loadSecurityStatus = useCallback(async () => {
    if (securityStatusLoadedRef.current || !user) {
      return securityStatus;
    }

    try {
      securityStatusLoadedRef.current = true;
      const securityData = await authService.getSecurityStatus();
      if (securityData.success && mountedRef.current) {
        setSecurityStatus(securityData);
        return securityData;
      }
    } catch (err) {
      console.warn("Could not load security status:", err);
    }
    return null;
  }, [user, securityStatus]);

  // FIXED: Initialize auth state on mount with better error handling
  useEffect(() => {
    const initializeAuth = async () => {
      // Prevent multiple simultaneous initializations
      if (initializationRef.current || loadingUserRef.current) {
        return;
      }

      try {
        initializationRef.current = true;
        loadingUserRef.current = true;

        const storedToken = authService.getToken();
        const storedUser = authService.getCurrentUserFromStorage();
        const storedSecurityLevel = authService.getSecurityLevel();

        if (storedToken && storedUser && authService.isAuthenticated()) {
          // Check if token is still valid
          if (isTokenValid(storedToken)) {
            safeSetState(setToken, storedToken);
            safeSetState(setUser, storedUser);
            safeSetState(setSecurityLevel, storedSecurityLevel);

            // Fetch fresh data in background without blocking UI
            safeSetTimeout(() => {
              fetchFreshUserDataInBackground().catch((error) => {
                console.warn("Background user data fetch failed:", error);
              });
            }, 1000);
          } else {
            // Token expired, try to refresh
            await handleTokenExpired();
          }
        } else {
          // No valid auth data, ensure clean state
          clearAuthState();
        }
      } catch (err) {
        console.error("Auth initialization error:", err);
        safeSetState(setError, err.message);
        clearAuthState();
      } finally {
        safeSetState(setLoading, false);
        loadingUserRef.current = false;
        initializationRef.current = false;
      }
    };

    initializeAuth();

    // Cleanup on unmount
    return cleanup;
  }, [
    safeSetState,
    safeSetTimeout,
    cleanup,
    isTokenValid,
    fetchFreshUserDataInBackground,
    handleTokenExpired,
    clearAuthState,
  ]);

  // Session event listeners
  useEffect(() => {
    const handleSessionExpiring = (event) => {
      if (mountedRef.current) {
        const minutesRemaining = event.detail.minutesRemaining || 5;
        setError(
          `Session expiring in ${minutesRemaining} minutes. Please save your work.`
        );

        // Auto-clear this warning after 30 seconds
        safeSetTimeout(() => {
          if (mountedRef.current) {
            setError(null);
          }
        }, 30000);
      }
    };

    const handleSessionExpired = () => {
      if (mountedRef.current) {
        setError("Session expired. Please log in again.");
        handleLogout();
      }
    };

    const handleRequire2FA = (event) => {
      if (mountedRef.current) {
        setLoginFlow("2fa_required");
        setLoginGuidance({
          type: "2fa_required",
          message: "Two-factor authentication required",
          data: event.detail,
        });
      }
    };

    const handleRequireVPN = (event) => {
      if (mountedRef.current) {
        setError("VPN connection required to access this resource");
        setLoginFlow("vpn_required");
        setLoginGuidance({
          type: "vpn_required",
          message: "VPN connection required",
          data: event.detail,
        });
      }
    };

    const handleRequireSecureLogin = () => {
      if (mountedRef.current) {
        setLoginFlow("secure_login_required");
        setLoginGuidance({
          type: "secure_login_required",
          message: "Enhanced security login required",
        });
      }
    };

    // Add event listeners
    window.addEventListener("sessionExpiring", handleSessionExpiring);
    window.addEventListener("sessionExpired", handleSessionExpired);
    window.addEventListener("require2FA", handleRequire2FA);
    window.addEventListener("requireVPN", handleRequireVPN);
    window.addEventListener("requireSecureLogin", handleRequireSecureLogin);

    return () => {
      window.removeEventListener("sessionExpiring", handleSessionExpiring);
      window.removeEventListener("sessionExpired", handleSessionExpired);
      window.removeEventListener("require2FA", handleRequire2FA);
      window.removeEventListener("requireVPN", handleRequireVPN);
      window.removeEventListener(
        "requireSecureLogin",
        handleRequireSecureLogin
      );
    };
  }, [safeSetTimeout]);

  // Periodic health check
  useEffect(() => {
    if (!user || !token) return;

    // Check auth status every 5 minutes
    const healthCheckInterval = safeSetInterval(async () => {
      try {
        const isStillAuthenticated = authService.isAuthenticated();
        if (!isStillAuthenticated && mountedRef.current) {
          console.warn("Auth token expired during health check");
          await handleTokenExpired();
        }
      } catch (error) {
        console.warn("Health check failed:", error);
      }
    }, 5 * 60 * 1000); // 5 minutes

    return () => {
      if (healthCheckInterval) {
        clearInterval(healthCheckInterval);
        intervalsRef.current.delete(healthCheckInterval);
      }
    };
  }, [user, token, safeSetInterval, handleTokenExpired]);

  // FIXED: Optimized login method with better error propagation
  const login = useCallback(
    async (username, password, recaptchaToken) => {
      if (loadingUserRef.current) {
        return { success: false, message: "Login already in progress" };
      }

      try {
        loadingUserRef.current = true;
        safeSetState(setLoading, true);
        safeSetState(setError, null); // Clear previous errors


        const response = await authService.login(username, password, recaptchaToken);

        if (!mountedRef.current) return response;


        if (response.success) {
          // Complete login success

          safeSetState(setToken, response.token || response.access_token);
          safeSetState(setUser, response.user);
          safeSetState(setSecurityLevel, response.security_level || "enhanced");

          clearLoginStates();

          safeSetTimeout(() => {
            loadSecurityStatus().catch(console.warn);
          }, 2000);

          return response;
        } else if (response.requires_2fa) {
          // FIXED: 2FA required - Backend already sent SMS, don't send again

          setPendingCredentials({
            username: username,
            password: password,
          });
          setLoginFlow("2fa_required");

          // FIXED: Use the SMS status from backend response
          setLoginGuidance({
            type: "2fa_required",
            message:
              response.message || "SMS verification code sent to your phone",
            user: response.user,
            available_2fa_methods: response.available_2fa_methods || ["sms"],
            sms_code_sent: response.sms_sent || false,
            expires_in: response.expires_in,
          });

          return response;
        } else if (
          response.setup_required &&
          response.setup_methods?.includes("2fa")
        ) {
          // 2FA setup required

          setPendingCredentials({
            username: username,
            password: password,
          });
          setLoginFlow("2fa_setup_required");
          setLoginGuidance({
            type: "2fa_setup_required",
            message: "Please set up two-factor authentication to continue",
            user: response.user,
            setup_methods: ["2fa"],
          });
          return response;
        } else if (response.rateLimited) {
          // FIXED: Set error state for rate limiting
          const errorMessage = `Too many login attempts. Please wait ${response.remainingTime} seconds.`;
          setError(errorMessage);
          return response;
        } else {
          // FIXED: Set error state for invalid credentials and other login failures
          const errorMessage =
            response.message || "Login failed. Please check your credentials.";
          setError(errorMessage);
          return response;
        }
      } catch (err) {
        console.error("🔐 Login error:", err);
        const errorMessage = err.message || "Login failed";
        if (mountedRef.current) {
          setError(errorMessage);
        }
        return { success: false, message: errorMessage };
      } finally {
        loadingUserRef.current = false;
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    },
    [safeSetState, safeSetTimeout, loadSecurityStatus, clearLoginStates]
  );

  // FIXED: Enhanced 2FA completion with better error handling
  const complete2FALogin = useCallback(
    async (verificationData) => {

      if (!pendingCredentials) {
        const errorMsg =
          "No pending credentials for 2FA. Please start login again.";
        console.error("❌", errorMsg);
        setError(errorMsg);
        setLoginFlow(null);
        return { success: false, message: errorMsg };
      }

      if (loadingUserRef.current) {
        return { success: false, message: "2FA login already in progress" };
      }

      try {
        loadingUserRef.current = true;
        safeSetState(setLoading, true);
        safeSetState(setError, null); // Clear previous errors


        // FIXED: Make sure we're calling the service correctly
        const response = await authService.loginWith2FA(
          pendingCredentials.username,
          pendingCredentials.password,
          verificationData.smsCode || null,
          verificationData.totpCode || null
        );

        if (!mountedRef.current) return response;


        if (response.success) {
          // FIXED: Complete successful 2FA login

          // Store auth data
          const authToken = response.access_token || response.token;
          safeSetState(setToken, authToken);
          safeSetState(setUser, response.user);
          safeSetState(setSecurityLevel, response.security_level || "enhanced");

          // Clear login flow states
          clearLoginStates();

          // Start background security status loading
          safeSetTimeout(() => {
            loadSecurityStatus().catch(console.warn);
          }, 2000);

          return response;
        } else if (response.rateLimited) {
          const errorMessage = `Too many attempts. Please wait ${response.remainingTime} seconds.`;
          setError(errorMessage);
          return response;
        } else {
          // FIXED: Set error state for 2FA failures
          const errorMessage = response.message || "SMS verification failed";
          setError(errorMessage);
          return response;
        }
      } catch (err) {
        console.error("🔐 2FA completion error:", err);
        const errorMessage = err.message || "SMS verification failed";
        if (mountedRef.current) {
          setError(errorMessage);
        }
        return { success: false, message: errorMessage };
      } finally {
        loadingUserRef.current = false;
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    },
    [
      pendingCredentials,
      safeSetState,
      safeSetTimeout,
      loadSecurityStatus,
      clearLoginStates,
    ]
  );

  // FIXED: SMS code request with better credential check and error handling
  const requestSMSCode = useCallback(async () => {
    if (!pendingCredentials) {
      const errorMsg = "No pending credentials. Please start login again.";
      console.error("❌", errorMsg);
      setError(errorMsg);
      return { success: false, message: errorMsg };
    }

    try {

      const response = await authService.requestSMSCode(
        pendingCredentials.username,
        pendingCredentials.password
      );

      if (mountedRef.current) {
        if (response.success) {
          setLoginGuidance((prev) => ({
            ...prev,
            sms_code_sent: true,
            expires_in: response.expires_in,
            message: response.message || "SMS code sent successfully",
          }));
          // Clear any previous errors
          setError(null);
        } else if (response.status === 429) {
          // FIXED: Handle rate limiting from backend
          const errorMessage = "Please wait 30 seconds between SMS requests.";
          setError(errorMessage);
        } else if (response.rateLimited) {
          const errorMessage =
            "Too many SMS requests. Please wait before trying again.";
          setError(errorMessage);
        } else {
          const errorMessage = response.message || "Failed to send SMS code";
          setError(errorMessage);
        }
      }

      return response;
    } catch (err) {
      console.error("📱 Request SMS error:", err);
      const errorMessage = err.message || "Failed to send SMS code";
      if (mountedRef.current) {
        // FIXED: Check for rate limiting error
        if (err.status === 429 || errorMessage.includes("wait")) {
          setError("Please wait 30 seconds between SMS requests.");
        } else {
          setError(errorMessage);
        }
      }
      return { success: false, message: errorMessage };
    }
  }, [pendingCredentials]);

  // Enhanced 2FA setup with proper error handling
  const setup2FA = useCallback(
    async (phoneNumber, countryCode = "+1", verificationCode = null) => {
      try {
        safeSetState(setLoading, true);
        safeSetState(setError, null);

        const response = await authService.setup2FA(
          phoneNumber,
          countryCode,
          verificationCode
        );

        if (response.success && !response.requires_verification) {
          // Only refresh security status if setup completed
          securityStatusLoadedRef.current = false; // Force reload next time it's needed
        }

        return response;
      } catch (err) {
        console.error("2FA setup error:", err);
        if (mountedRef.current) {
          setError(err.message || "2FA setup failed");
        }
        throw err;
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    },
    [safeSetState]
  );

  // Enhanced verify 2FA setup
  const verify2FASetup = useCallback(
    async (code) => {
      try {
        safeSetState(setLoading, true);
        safeSetState(setError, null);

        const response = await authService.verify2FASetup(code);

        if (response.success) {
          // Reset security status cache
          securityStatusLoadedRef.current = false;
        }

        return response;
      } catch (err) {
        console.error("2FA verification error:", err);
        if (mountedRef.current) {
          setError(err.message || "2FA verification failed");
        }
        throw err;
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    },
    [safeSetState]
  );

  // 2FA setup using a limited-scope setup token (mandatory 2FA enrollment)
  const setup2FAWithToken = useCallback(
    async (
      setupToken,
      phoneNumber,
      countryCode = "+1",
      verificationCode = null
    ) => {
      try {
        safeSetState(setLoading, true);
        safeSetState(setError, null);

        const response = await authService.setup2FAWithToken(
          setupToken,
          phoneNumber,
          countryCode,
          verificationCode
        );

        return response;
      } catch (err) {
        console.error("2FA setup (with token) error:", err);
        if (mountedRef.current) {
          setError(err.message || "2FA setup failed");
        }
        throw err;
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    },
    [safeSetState]
  );

  // Verify 2FA setup using a limited-scope setup token
  const verify2FASetupWithToken = useCallback(
    async (setupToken, code) => {
      try {
        safeSetState(setLoading, true);
        safeSetState(setError, null);

        const response = await authService.verify2FASetupWithToken(
          setupToken,
          code
        );

        return response;
      } catch (err) {
        console.error("2FA verification (with token) error:", err);
        if (mountedRef.current) {
          setError(err.message || "2FA verification failed");
        }
        throw err;
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    },
    [safeSetState]
  );

  // VPN setup
  const setupVPNAccess = useCallback(
    async (allowedIpRanges) => {
      try {
        safeSetState(setLoading, true);
        safeSetState(setError, null);

        const response = await authService.setupVPNAccess(allowedIpRanges);

        if (response.success) {
          securityStatusLoadedRef.current = false; // Force reload
        }

        return response;
      } catch (err) {
        console.error("VPN setup error:", err);
        if (mountedRef.current) {
          setError(err.message || "VPN setup failed");
        }
        throw err;
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    },
    [safeSetState]
  );

  // Disable 2FA
  const disable2FA = useCallback(
    async (password) => {
      try {
        safeSetState(setLoading, true);
        safeSetState(setError, null);

        const response = await authService.disable2FA(password);

        if (response.success) {
          securityStatusLoadedRef.current = false; // Force reload
        }

        return response;
      } catch (err) {
        console.error("2FA disable error:", err);
        if (mountedRef.current) {
          setError(err.message || "Failed to disable 2FA");
        }
        throw err;
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    },
    [safeSetState]
  );

  // FIXED: Enhanced logout with proper cleanup
  const handleLogout = useCallback(() => {

    try {
      // Don't wait for server response
      authService.logout();
    } catch (err) {
      console.warn("Error during logout:", err);
    } finally {
      clearAuthState();
      clearLoginStates();
      if (mountedRef.current) {
        setError(null);
      }

      // FIXED: Redirect to login page after logout
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }
  }, [clearAuthState, clearLoginStates]);

  // Update user data
  const updateUser = useCallback(
    (newUserData) => {
      if (mountedRef.current && user) {
        const updatedUser = { ...user, ...newUserData };
        setUser(updatedUser);
        // Update stored user data
        authService.clearCache(); // Clear cache so next fetch gets fresh data
      }
    },
    [user]
  );

  // Get security status (lazy loaded)
  const getSecurityStatus = useCallback(async () => {
    if (securityStatus) {
      return securityStatus;
    }
    return await loadSecurityStatus();
  }, [securityStatus, loadSecurityStatus]);

  // FIXED: Clear error method that actually clears the error
  const clearError = useCallback(() => {
    if (mountedRef.current) {
      setError(null);
    }
  }, []);

  // Security checking functions (memoized)
  const isSecureSession = useMemo(() => {
    return securityLevel === "maximum" || securityLevel === "enhanced";
  }, [securityLevel]);

  const has2FAEnabled = useMemo(() => {
    return (
      securityStatus?.sms_2fa_enabled ||
      securityStatus?.totp_2fa_enabled ||
      false
    );
  }, [securityStatus]);

  const isVPNRequired = useMemo(() => {
    return securityStatus?.vpn_required || false;
  }, [securityStatus]);

  const hasAPIKey = useMemo(() => {
    return securityStatus?.api_key_configured || false;
  }, [securityStatus]);

  // Role checking functions (memoized)
  const isSuperAdmin = useMemo(() => {
    return user?.role === "super_admin";
  }, [user?.role]);

  const isAdmin = useMemo(() => {
    return (
      user && ["super_admin", "admin", "facility_admin"].includes(user.role)
    );
  }, [user?.role]);

  const isFacilityAdmin = useMemo(() => {
    return (
      user && ["super_admin", "admin", "facility_admin"].includes(user.role)
    );
  }, [user?.role]);

  const isMedicalProfessional = useMemo(() => {
    return user?.role === "medical_professional";
  }, [user?.role]);

  // FIXED: Add missing permission functions that Layout.js is calling
  const canViewAnalytics = useMemo(() => {
    return (
      user && ["super_admin", "admin", "facility_admin"].includes(user.role)
    );
  }, [user?.role]);

  const canManageSettings = useMemo(() => {
    return (
      user && ["super_admin", "admin", "facility_admin"].includes(user.role)
    );
  }, [user?.role]);

  const canManageMedicalProfessionals = useMemo(() => {
    return (
      user && ["super_admin", "admin", "facility_admin"].includes(user.role)
    );
  }, [user?.role]);

  const canViewFacility = useMemo(() => {
    if (!user) return false;
    if (user.role === "super_admin") return true;
    return !!user.facility_id;
  }, [user?.role, user?.facility_id]);

  const canManageFacilities = useMemo(() => {
    return user?.role === "super_admin";
  }, [user?.role]);

  // FIXED: Add canManageUsers function that UserManagement is looking for
  const canManageUsers = useMemo(() => {
    return user?.role === "super_admin";
  }, [user?.role]);

  // Permission functions (memoized for performance)
  const permissions = useMemo(() => {
    if (!user) return {};

    return {
      // FACILITY PERMISSIONS
      canManageFacilities: user.role === "super_admin",
      canViewFacility: (facilityId = null) => {
        if (user.role === "super_admin") return true;
        if (!facilityId) return !!user.facility_id;
        return user.facility_id === facilityId;
      },
      canEditFacility: (facilityId = null) => {
        if (user.role === "super_admin") return true;
        if (!facilityId) return false;
        return (
          user.facility_id === facilityId &&
          (user.role === "admin" || user.role === "facility_admin")
        );
      },
      canDeleteFacility: user.role === "super_admin",
      canCreateFacility: user.role === "super_admin",

      // USER PERMISSIONS
      canManageUsers: user.role === "super_admin",
      canCreateUsers: user.role === "super_admin",
      canEditUsers: user.role === "super_admin",
      canDeleteUsers: user.role === "super_admin",

      // MEDICAL PROFESSIONAL PERMISSIONS
      canManageMedicalProfessionals: [
        "super_admin",
        "admin",
        "facility_admin",
      ].includes(user.role),
      canCreateMedicalProfessionals: [
        "super_admin",
        "admin",
        "facility_admin",
      ].includes(user.role),
      canEditMedicalProfessionals: [
        "super_admin",
        "admin",
        "facility_admin",
      ].includes(user.role),
      canDeleteMedicalProfessionals: user.role === "super_admin",

      // ORDER PERMISSIONS
      canCreateOrders: ["super_admin", "admin", "facility_admin"].includes(
        user.role
      ),
      canViewOrders: [
        "super_admin",
        "admin",
        "facility_admin",
        "medical_professional",
      ].includes(user.role),
      canEditOrders: [
        "super_admin",
        "admin",
        "facility_admin",
        "medical_professional",
      ].includes(user.role),
      canDeleteOrders: user.role === "super_admin",

      // PATIENT PERMISSIONS
      canEditPatients: ["super_admin", "admin", "facility_admin"].includes(
        user.role
      ),
      canCreatePatients: ["super_admin", "admin", "facility_admin"].includes(
        user.role
      ),
      canViewPatients: [
        "super_admin",
        "admin",
        "facility_admin",
        "medical_professional",
      ].includes(user.role),
      canDeletePatients: user.role === "super_admin",

      // ROUTE PERMISSIONS
      canOptimizeRoutes: ["super_admin", "admin", "facility_admin"].includes(
        user.role
      ),
      canViewRoutes: ["super_admin", "admin", "facility_admin"].includes(
        user.role
      ),
      canManageRoutes: ["super_admin", "admin", "facility_admin"].includes(
        user.role
      ),

      // ANALYTICS AND SETTINGS
      canViewAnalytics: ["super_admin", "admin", "facility_admin"].includes(
        user.role
      ),
      canManageSettings: ["super_admin", "admin", "facility_admin"].includes(
        user.role
      ),

      // DASHBOARD
      canViewDashboard: true,
    };
  }, [user]);

  // FIXED: Authentication status with better checks
  const isAuthenticated = useMemo(() => {
    const hasValidToken = !!(token && authService.isAuthenticated());
    const hasUser = !!user;


    return hasValidToken && hasUser;
  }, [user, token]);

  // Login flow helpers (memoized)
  const loginFlowHelpers = useMemo(() => {
    return {
      getLoginFlowGuidance: () => loginGuidance,
      getCurrentLoginFlow: () => loginFlow,
      hasPendingCredentials: !!pendingCredentials,
      isLoginInProgress: !!loginFlow && loginFlow !== "invalid",
      needsSecuritySetup: loginFlow === "setup_required",
      needs2FA: loginFlow === "2fa_required",
      needsVPN: loginFlow === "vpn_required",
      needsSecureLogin: loginFlow === "secure_login_required",
      getSetupRequirements: () =>
        loginGuidance?.setup_needed || loginGuidance?.setup_methods || [],
    };
  }, [loginGuidance, loginFlow, pendingCredentials]);

  // Generic role/permission checking functions
  const hasRole = useCallback(
    (role) => {
      return user?.role === role;
    },
    [user?.role]
  );

  const hasAnyRole = useCallback(
    (roles) => {
      return user && roles.includes(user.role);
    },
    [user?.role]
  );

  // Advanced permission checking
  const hasPermission = useCallback(
    (permission) => {
      if (!user) return false;

      const rolePermissions = {
        super_admin: ["*"], // All permissions
        admin: [
          "manage_users",
          "manage_facilities",
          "view_analytics",
          "manage_orders",
        ],
        facility_admin: [
          "manage_facility_users",
          "manage_orders",
          "view_facility_analytics",
        ],
        medical_professional: ["view_orders", "update_orders", "view_patients"],
        user: ["view_own_data"],
      };

      const userPermissions = rolePermissions[user.role] || [];
      return (
        userPermissions.includes("*") || userPermissions.includes(permission)
      );
    },
    [user]
  );

  // Session management helpers
  const getSessionInfo = useCallback(() => {
    if (!token) return null;

    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return {
        expiresAt: new Date(payload.exp * 1000),
        issuedAt: new Date(payload.iat * 1000),
        timeUntilExpiry: payload.exp * 1000 - Date.now(),
        isExpired: payload.exp * 1000 < Date.now(),
      };
    } catch {
      return null;
    }
  }, [token]);

  // FIXED: Context value with all methods properly exposed
  const contextValue = useMemo(
    () => ({
      // State
      user,
      loading,
      error,
      token,
      securityStatus,
      securityLevel,

      // Login flow states
      loginFlow,
      loginGuidance,
      pendingCredentials: !!pendingCredentials,

      // Actions
      login,
      complete2FALogin,
      requestSMSCode,
      setup2FA,
      verify2FASetup,
      setup2FAWithToken,
      verify2FASetupWithToken,
      setupVPNAccess,
      disable2FA,
      logout: handleLogout,
      updateUser,
      clearError,
      clearLoginStates,
      getSecurityStatus,

      // Authentication status
      isAuthenticated,
      role: user?.role || null,
      facilityId: user?.facility_id || null,

      // Security status (computed from cached data when possible)
      isSecureSession,
      has2FAEnabled,
      isVPNRequired,
      hasAPIKey,

      // Login flow helpers
      ...loginFlowHelpers,

      // Role checking methods
      isAdmin,
      isSuperAdmin,
      isFacilityAdmin,
      isMedicalProfessional,

      // FIXED: Missing permission functions that Layout.js is calling
      canViewAnalytics,
      canManageSettings,
      canManageMedicalProfessionals,
      canViewFacility,
      canManageFacilities,
      canManageUsers, // FIXED: Added missing function

      // Permission methods (spread the permissions object)
      ...permissions,

      // Generic methods
      hasRole,
      hasAnyRole,
      hasPermission,

      // Session management
      getSessionInfo,

      // Debug helpers for development
      _debug: {
        loadingUserRef: loadingUserRef.current,
        securityStatusLoadedRef: securityStatusLoadedRef.current,
        initializationRef: initializationRef.current,
        mountedRef: mountedRef.current,
        activeTimeouts: timeoutsRef.current.size,
        activeIntervals: intervalsRef.current.size,
      },
    }),
    [
      user,
      loading,
      error,
      token,
      securityStatus,
      securityLevel,
      loginFlow,
      loginGuidance,
      pendingCredentials,
      login,
      complete2FALogin,
      requestSMSCode,
      setup2FA,
      verify2FASetup,
      setup2FAWithToken,
      verify2FASetupWithToken,
      setupVPNAccess,
      disable2FA,
      handleLogout,
      updateUser,
      clearError,
      clearLoginStates,
      getSecurityStatus,
      isAuthenticated,
      isSecureSession,
      has2FAEnabled,
      isVPNRequired,
      hasAPIKey,
      loginFlowHelpers,
      isAdmin,
      isSuperAdmin,
      isFacilityAdmin,
      isMedicalProfessional,
      canViewAnalytics,
      canManageSettings,
      canManageMedicalProfessionals,
      canViewFacility,
      canManageFacilities,
      canManageUsers,
      permissions,
      hasRole,
      hasAnyRole,
      hasPermission,
      getSessionInfo,
    ]
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
